import React, { useEffect, useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { X, Tag, CircleX, Check, Loader2, CloudUpload, ChevronUp, ChevronDown } from "lucide-react";
import "./DocumentModal.css";
import {
  scrapReferenceUrl,
  uploadFilesToApi,
  executeAgentStream,
  executeManualAgent,
} from "../../utils/Product";
import { resolveOutputTableNameFromAutomation } from "../../services/automationOutputService";
import {
  resolveAutomationAgentId,
  resolveAutomationQuery,
  buildManualAgentStreamOptions,
} from "../../pages/Automation/automationAgentConfig";
import { useStreamingJson } from "../../utils/useStreamingJson";
import { generateSessionId } from "../../utils/session";
import { useDropzone } from "react-dropzone";
import { getFileIcon } from "../../utils/Product";
import { useCompany } from "../Context/companyContext";
import axiosInstance from "../../utils/api";

const toast = {
  info: (msg) => console.log('INFO:', msg),
  success: (msg) => console.log('SUCCESS:', msg),
  error: (msg) => console.error('ERROR:', msg),
};

const Select = ({ options, value, onChange, ...props }) => {
  return (
    <select
      value={value?.value || value || ''}
      onChange={(e) => {
        const selected = options?.find(opt => opt.value === e.target.value);
        onChange?.(selected);
      }}
      {...props}
      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
    >
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

const EMPTY_DOC_LIST = [];
const EMPTY_KNOWLEDGE_DOCS = [];
const EMPTY_GROUPED_DOCUMENTS = [];

const DocumentModal = ({
  show,
  onHide,
  agent = null,
  automation = null,
  initialTags = "",
  initialDocList = EMPTY_DOC_LIST,
  companyKnowledgeDocs = EMPTY_KNOWLEDGE_DOCS,
  groupedDocuments = EMPTY_GROUPED_DOCUMENTS,
  editingRowData = null,
  onSuccess = null,
  onAgentChunk = null,
}) => {
  const { companyId } = useCompany();
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [tags, setTags] = useState(initialTags || "");
  const [tagList, setTagList] = useState([]);
  const [docList, setDocList] = useState([]);
  const [referenceUrls, setReferenceUrls] = useState([""]);
  const [scrapingLoadingArr, setScrapingLoadingArr] = useState([false]);
  const [scrapingSuccessArr, setScrapingSuccessArr] = useState([false]);
  const [uid, setUid] = useState("");
  const [isScrapingForEdit, setIsScrapingForEdit] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [isLoadingIntegration, setIsLoadingIntegration] = useState(false);
  const [showFieldValidation, setShowFieldValidation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [integrationOptions, setIntegrationOptions] = useState([]);
  const [fileUploadProgress, setFileUploadProgress] = useState({});
  const [fileUploadStatus, setFileUploadStatus] = useState({});

  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const { run: runStreaming, isStreaming: isAgentStreaming } = useStreamingJson();

  const generateUID = () => {
    const timestamp = Date.now().toString();
    const randomPart = Math.random().toString(36).substring(2, 6);
    const newUID = `${timestamp}-${randomPart}`;
    setUid(newUID);
    return newUID;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/jpg': ['.jpg']
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      
      // Initialize upload status for new files
      acceptedFiles.forEach(file => {
        const fileId = file.name;
        setFileUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        setFileUploadStatus(prev => ({ ...prev, [fileId]: 'uploading' }));
      });
      
      // Simulate upload progress (you can replace this with actual upload progress)
      acceptedFiles.forEach((file) => {
        const fileId = file.name;
        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 30;
          if (progress >= 100) {
            progress = 100;
            setFileUploadStatus(prev => ({ ...prev, [fileId]: 'done' }));
            clearInterval(interval);
          }
          setFileUploadProgress(prev => ({ ...prev, [fileId]: progress }));
        }, 200);
      });
    },
  });

  useEffect(() => {
    if (initialTags) {
      setTagList(initialTags.split(",").map((tag) => tag.trim()));
    }
  }, [initialTags]);

  useEffect(() => {
    if (!initialDocList?.length) return;

    if (Array.isArray(initialDocList)) {
      setDocList(initialDocList);
    } else if (typeof initialDocList === "string") {
      setDocList(initialDocList.split(",").map((doc) => doc.trim()));
    }
  }, [initialDocList]);

  useEffect(() => {
    if (!show) {
      setSelectedIntegration("");
      setIntegrations([]);
      setShowFieldValidation(false);
      setSearchTerm("");
      setIsDropdownOpen(false);
      setUid("");
      return;
    }

    setUploadedFiles([]);
    setFileUploadProgress({});
    setFileUploadStatus({});
    setUid(`${Date.now()}-${Math.random().toString(36).substring(2, 6)}`);
  }, [show]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);


  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const shortenFileName = (fileName) => {
    return fileName.length > 10 ? `${fileName.slice(0, 10)}...` : fileName;
  };

  const getFileSize = (file) => {
    return (file.size / 1024).toFixed(2) + " KB";
  };

  const handleTagInput = (event) => {
    if (event.key === "Enter" && tags.trim()) {
      setTagList((prevTags) => [...prevTags, tags.trim()]);
      setTags("");
    }
  };

  const handleRemoveTag = (index) => {
    const newTags = tagList.filter((_, i) => i !== index);
    setTagList(newTags);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setUploadedFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    event.target.value = null;
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    const fileId = uploadedFiles[index]?.name || index;
    setFileUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
    setFileUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileId];
      return newStatus;
    });
  };

  const handleMoveFile = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === uploadedFiles.length - 1) return;
    
    const newFiles = [...uploadedFiles];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];
    setUploadedFiles(newFiles);
  };

  const handleScrapAtIndex = async (idx) => {
    if (!referenceUrls[idx]) return;
    
    const newLoading = [...scrapingLoadingArr];
    const newSuccess = [...scrapingSuccessArr];
    newLoading[idx] = true;
    newSuccess[idx] = false;
    setScrapingLoadingArr(newLoading);
    setScrapingSuccessArr(newSuccess);
  
    let generatedUID = uid || generateUID();
  
    const payload = {
      request_type: agent?.request_type || "agent_processing",
      additional_data: {
        agent_id: agent?.id || null,
        tags: tagList,
        documents: docList,
        url: referenceUrls[idx],
        uid: generatedUID,
        company_id: companyId,
      },
    };
  
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await scrapReferenceUrl(payload);
        newSuccess[idx] = true;
        break; 
      } catch (e) {
        console.error(`Attempt ${attempt} failed for index ${idx}`, e);
        if (attempt === 3) {
          newSuccess[idx] = false;
        }
      }
    }
  
    newLoading[idx] = false;
    setScrapingLoadingArr([...newLoading]);
    setScrapingSuccessArr([...newSuccess]);
  };

  const getIntegrationConfig = async (integrationType) => {
    try {
      const selected = integrationOptions.find(opt => opt.value === integrationType);
      const nameForApi = selected?.label || integrationType;
      const response = await axiosInstance.get(`/api/v1/integrations/${nameForApi}`);
      const data = response.data?.data;
      
      if (!data) return null;

      let fields = Array.isArray(data?.notes?.fields) ? [...data.notes.fields] : [];

      if (Array.isArray(data.actions) && data.actions.length > 0) {
        const actionIndex = fields.findIndex(f => f.id === 'action');
        const actionOptions = data.actions.map(a => ({ label: a, value: a }));
        if (actionIndex >= 0) {
          fields[actionIndex] = {
            ...fields[actionIndex],
            type: 'select',
            options: actionOptions,
            required: fields[actionIndex].required ?? true,
          };
        } else {
          fields.unshift({
            id: 'action',
            type: 'select',
            label: 'Select Action',
            options: actionOptions,
            required: true,
          });
        }
      }

      return {
        integration_type: data.name,
        fields,
      };
    } catch (error) {
      console.error('Error fetching integration config:', error);
      return null;
    }
  };

  const handleIntegrationSelect = (selectedOption) => {
    setSelectedIntegration(selectedOption?.value || '');
  };

  const handleConfigureIntegration = async () => {
    if (!selectedIntegration) return;

    setIsLoadingIntegration(true);
    
    try {
      const config = await getIntegrationConfig(selectedIntegration);
      if (!config) {
        toast.error('Failed to fetch integration configuration');
        return;
      }
      
      const initialFields = {};
      config.fields.forEach(field => {
        initialFields[field.id] = '';
      });

      const newIntegration = {
        id: Date.now(),
        type: selectedIntegration,
        config: config,
        fields: initialFields
      };

      setIntegrations(prev => [...prev, newIntegration]);
      setSelectedIntegration(''); 
    } catch (error) {
      console.error('Error fetching integration config:', error);
      toast.error('Failed to configure integration');
    } finally {
      setIsLoadingIntegration(false);
    }
  };

  const handleIntegrationFieldChange = (integrationId, fieldId, value) => {
    setIntegrations(prev => 
      prev.map(integration => 
        integration.id === integrationId 
          ? {
              ...integration,
              fields: {
                ...integration.fields,
                [fieldId]: value
              }
            }
          : integration
      )
    );
    if (showFieldValidation) {
      setShowFieldValidation(false);
    }
  };

  const removeIntegration = (integrationId) => {
    setIntegrations(prev => prev.filter(integration => integration.id !== integrationId));
  };

  const isIntegrationValid = (integration) => {
    if (!integration.config?.fields) return true;
    
    return integration.config.fields.every(field => {
      if (!field.required) return true;
      const fieldValue = integration.fields[field.id];
      return fieldValue && fieldValue.toString().trim() !== '';
    });
  };

  const areAllIntegrationsValid = () => {
    return integrations.every(integration => isIntegrationValid(integration));
  };

  const docOptions = React.useMemo(() => {
    return groupedDocuments.length > 0 
      ? groupedDocuments 
      : companyKnowledgeDocs.length > 0 
      ? companyKnowledgeDocs.map((doc, index) => {
          const docValue = doc.value || doc.url || doc.name || doc;
          const docLabel = doc.label || doc.name || (typeof doc === 'string' ? doc.split("/").pop() : `Document ${index + 1}`);
          
          return {
            value: docValue,
            label: docLabel,
            source: doc.source || 'knowledge_base',
            contentType: doc.contentType || null
          };
        })
      : [];
  }, [groupedDocuments, companyKnowledgeDocs]);

  const filteredDocOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return docOptions;
    }

    return docOptions.map(group => ({
      ...group,
      options: group.options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(group => group.options.length > 0);
  }, [docOptions, searchTerm]);

  const getResourceInfo = () => {
    const selectedGeneratedDocs = docList
      .map(selectedValue => {
        return docOptions
          .flatMap(group => group.options || [])
          .find(option => option.value === selectedValue);
      })
      .filter(doc => doc && doc.source === 'generated_contents');

    if (selectedGeneratedDocs.length > 0) {
      const firstDoc = selectedGeneratedDocs[0];
      const resourceId = firstDoc.value || firstDoc.id;
      const numericId = resourceId ? resourceId.toString().split('_').pop() : null;
      
      return {
        resource_type: firstDoc.contentType,
        resource_id: numericId
      };
    }
    return null;
  };

  const handleFileUpload = async () => {
    if (integrations.length > 0 && !areAllIntegrationsValid()) {
      setShowFieldValidation(true);
      toast.error('Please fill all required fields in the integrations');
      return;
    }
    
    setShowFieldValidation(false);
    setIsUploading(true);
    let generatedUID = uid || generateUID();
    const generatedSessionId = generateSessionId();

    const formData = new FormData();

    uploadedFiles.forEach((file) => {
      formData.append("file[]", file);
    });

    const resourceInfo = getResourceInfo();
    if (resourceInfo) {
      formData.append("resource_type", resourceInfo.resource_type);
      formData.append("resource_id", resourceInfo.resource_id);
    }

    const knowledgeBaseDocuments = docList.filter(selectedValue => {
      const doc = docOptions
        .flatMap(group => group.options || [])
        .find(option => option.value === selectedValue);
      return doc && doc.source === 'knowledge_base';
    });

    const jsonPayload = {
      request_type: agent?.request_type || "agent_processing",
      agent_id: agent?.id || null,
      ...(resourceInfo && { resource_type: resourceInfo.resource_type, resource_id: resourceInfo.resource_id }),
      additional_data: {
        tags: tagList,
        documents: knowledgeBaseDocuments,
        uid: generatedUID,
        session_id: generatedSessionId,
        company_id: companyId,
      },
    };

    formData.append("request_json", JSON.stringify(jsonPayload));
    
    const tempFilesToCleanup = [];
    
    try {
      const googleIntegrations = integrations.filter(integration => {
        const type = integration.type?.toLowerCase() || '';
        const configType = integration.config?.integration_type?.toLowerCase() || '';
        return type.includes('google') || configType.includes('google');
      });
      
      if (googleIntegrations.length > 0) {
        for (const integration of googleIntegrations) {
          try {
            const { fields } = integration;
            const sheetUrl = fields?.sheet_url || fields?.sheetUrl;
            const dataRange = fields?.data_range || fields?.dataRange;
            const customInstruction = fields?.custom_instructions || fields?.customInstructions || fields?.custom_instruction;
            const action = fields?.action;
            
            if (sheetUrl) {
              const response = await axiosInstance.post('/api/v1/integrations/google-sheets/fetch', {
                sheet_url: sheetUrl,
                data_range: dataRange || '',
                custom_instruction: customInstruction || '',
                actions: action ? [action] : []
              });
              
              const sheetData = response.data?.data;
              
              if (sheetData?.download_url) {
                try {
                  const authToken = localStorage.getItem('token');
                  const downloadResponse = await fetch(sheetData.download_url, {
                    headers: authToken ? { 'Authorization': authToken } : {},
                  });
                  
                  if (!downloadResponse.ok) {
                    throw new Error(`Failed to download file: ${downloadResponse.status}`);
                  }
                  
                  const blob = await downloadResponse.blob();
                  const contentType = downloadResponse.headers.get('content-type') || blob.type || '';
                  const fileExtension = contentType.includes('csv') || contentType.includes('text/csv') 
                    ? 'csv' 
                    : contentType.includes('xlsx') || contentType.includes('spreadsheetml')
                    ? 'xlsx'
                    : 'xlsx';
                  const fileName = `google_sheets_${Date.now()}.${fileExtension}`;
                  const file = new File([blob], fileName, { 
                    type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                  });
                  
                  formData.append("file[]", file);
                  tempFilesToCleanup.push(file);
                  
                  toast.success(`Successfully fetched and added Google Sheets data`);
                } catch (downloadError) {
                  console.error('Error downloading Google Sheets file:', downloadError);
                  toast.error(`Failed to download Google Sheets file: ${downloadError.message}`);
                }
              } else {
                toast.success(`Successfully fetched data from Google Sheets`);
              }
            }
          } catch (error) {
            console.error('Error fetching Google Sheets data:', error);
            toast.error(`Failed to fetch data from Google Sheets: ${error.message}`);
          }
        }
      }
      
      if (automation?.id && uploadedFiles.length > 0) {
        const agentId = resolveAutomationAgentId(automation);
        if (!agentId) {
          toast.error("This automation does not have an automation agent configured.");
          setIsUploading(false);
          return;
        }

        const query =
          tagList.length > 0
            ? tagList.join(", ")
            : resolveAutomationQuery(automation);
        const outputTableName = resolveOutputTableNameFromAutomation(automation);
        const manualOptions = buildManualAgentStreamOptions(automation, {
          outputTableName,
        });

        setIsUploading(false);
        onHide();

        runStreaming(async (onChunk) => {
          return await executeManualAgent(
            agentId,
            generatedSessionId,
            query,
            onChunk,
            {},
            {
              ...manualOptions,
              files: uploadedFiles,
            }
          );
        })
          .then(() => {
            toast.success("All processing completed!");
            if (onSuccess) {
              onSuccess(null, {
                tags: tagList.join(", "),
                documents: docList,
                uid: generatedUID,
                uploadedFiles: uploadedFiles,
              });
            }
          })
          .catch((error) => {
            console.error("Manual automation execution error:", error);
            toast.error("Error during processing. Please try again.");
          });
      } else if (agent?.id && uploadedFiles.length > 0) {
        const query = tagList.length > 0 ? tagList.join(", ") : "Extract the details from file";

        setIsUploading(false);
        onHide();

        runStreaming(async (onChunk) => {
          return await executeAgentStream(
            agent.id,
            agent.name || null,
            generatedSessionId,
            query,
            uploadedFiles,
            onChunk,
            (parsedChunk) => {
              if (onAgentChunk) {
                onAgentChunk(parsedChunk);
              }
              if (parsedChunk.status === "finish") {
                toast.success("Processing completed successfully!");
              } else if (parsedChunk.status === "completed") {
                toast.success("Invoice processed successfully");
              } else if (parsedChunk.status === "complete") {
                toast.info("Item processed successfully");
              } else if (parsedChunk.status === "start") {
                toast.info("Processing started...");
              } else if (parsedChunk.status === "processing") {
                toast.info("Processing invoice...");
              } else if (parsedChunk.status === "analysing_invoice") {
                toast.info("Analysing invoice...");
              }
            }
          );
        })
          .then((result) => {
            toast.success("All processing completed!");
            if (onSuccess) {
              onSuccess(null, {
                tags: tagList.join(", "),
                documents: docList,
                uid: generatedUID,
                uploadedFiles: uploadedFiles,
              });
            }
          })
          .catch((error) => {
            console.error("Agent execution error:", error);
            toast.error("Error during processing. Please try again.");
          });
      } else {
        const response = await uploadFilesToApi(formData);
        
        if (onSuccess) {
          onSuccess(response, {
            tags: tagList.join(", "),
            documents: docList,
            uid: generatedUID,
            uploadedFiles: uploadedFiles,
          });
        }
        
        onHide();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      if (error.response && error.response.data) {
        toast.error(error.response.data.error || JSON.stringify(error.response.data));
      } else {
        toast.error(error.message || "File upload failed. Please try again.");
      }
    } finally {
      setIsUploading(false);
      tempFilesToCleanup.length = 0;
    }
  };

  const onSkipStep = async () => {
    let generatedUID = uid || generateUID();
    
    if (onSuccess) {
      onSuccess(null, {
        tags: tagList.join(", "),
        documents: docList,
        uid: generatedUID,
        uploadedFiles: [],
      });
    }
    
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={isScrapingForEdit ? undefined : onHide}
      centered
      className="document-upload-modal"
      backdrop={isScrapingForEdit ? 'static' : true}
      keyboard={!isScrapingForEdit}
    >
      <Modal.Header className="upload-modal-header">
        <div className="upload-header-content">
          <Modal.Title className="upload-modal-title">
            Upload Invoices
          </Modal.Title>
          <p className="upload-modal-description">
            Effortlessly upload your invoices for accounts payable automation to extract key information.
          </p>
        </div>
        <button 
          className="close-button" 
          onClick={onHide}
          disabled={isScrapingForEdit}
          style={{ opacity: isScrapingForEdit ? 0.5 : 1, cursor: isScrapingForEdit ? 'not-allowed' : 'pointer' }}
        >
          <X size={18} color="black" />
        </button>
      </Modal.Header>

      <Modal.Body>
        <div
          {...getRootProps({ className: "upload-zone" })}
          onClick={openFileDialog}
        >
          <input
            {...getInputProps()}
            type="file"
            multiple
            accept=".pdf,.txt,.doc,.csv,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            ref={fileInputRef}
          />
          <CloudUpload size={48} className="upload-cloud-icon" />
          <p className="upload-text">
            Drop files here or click to browse
          </p>
          <p className="file-types">
          </p>
        </div>

        {uploadedFiles.length > 0 && (
          <div className="uploaded-files-section">
            <h6 className="uploaded-files-title">
              Uploaded Files ({uploadedFiles.length})
            </h6>
            <div className="uploaded-files">
              {uploadedFiles.map((file, index) => {
                const fileId = file.name;
                const progress = fileUploadProgress[fileId] || 0;
                const status = fileUploadStatus[fileId] || 'done';
                const isUploading = status === 'uploading';
                
                return (
                  <div key={index} className="uploaded-file-item">
                    <div className="file-info-section">
                      <img
                        src={getFileIcon(file.type)}
                        alt="File"
                        className="file-icon-small"
                      />
                      <div className="file-details">
                        <p className="file-name">{file.name}</p>
                        <div className="file-meta">
                          <span className="file-size">{getFileSize(file)}</span>
                          {status === 'done' && (
                            <span className="file-status done">
                              <Check size={14} />
                              Done
                            </span>
                          )}
                          {isUploading && (
                            <span className="file-status uploading">
                              <Loader2 size={14} className="animate-spin" />
                              Uploading
                            </span>
                          )}
                        </div>
                        {isUploading && (
                          <div className="upload-progress-bar">
                            <div 
                              className="upload-progress-fill"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="file-actions">
                      <button
                        onClick={() => handleMoveFile(index, 'up')}
                        className="file-action-btn"
                        disabled={index === 0}
                        title="Move up"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={() => handleMoveFile(index, 'down')}
                        className="file-action-btn"
                        disabled={index === uploadedFiles.length - 1}
                        title="Move down"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="file-action-btn remove"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="upload-modal-footer">
        <button
          className="start-automation-button"
          onClick={handleFileUpload}
          disabled={isUploading || isScrapingForEdit}
        >
          {isUploading ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" />
              Processing...
            </>
          ) : isScrapingForEdit ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" />
              Scraping...
            </>
          ) : (
            "Start Automation"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentModal;
